import { useState, useMemo, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import { stockLevels, parts, customerAssignedLocations } from '../../services/dataClient';
import { calculateDistance, estimateShippingCost, estimateDeliveryDays } from '../../utils/distance';

const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);

// Column order: Location | Part # | Distance | Est. Shipping | Est. Delivery | Avail. Qty | Select
const COLS = '1.6fr 1fr 90px 110px 100px 80px 80px';
const HEADERS = ['Location', 'Part #', 'Distance', 'Est. Shipping', 'Est. Delivery', 'Avail. Qty', ''];

// Inline abbreviation tag — same logic as PartOrdersPage OriginTag
const ABBR = { FSL: 'FSL', Warehouse: 'WH', OnSite: 'OS' };
function LocationTag({ locationType }) {
  if (!locationType) return null;
  const isFsl = locationType === 'FSL';
  return (
    <Box
      component="span"
      sx={{ display: 'inline-flex',
        alignItems: 'center',
        px: '5px',
        py: '1px',
        borderRadius: '3px',
        fontSize: '0.6875rem',
        fontWeight: 700,
        lineHeight: 1.4,
        flexShrink: 0,
        backgroundColor: isFsl ? '#E0F2F1' : '#ECEFF1',
        color: isFsl ? '#00695C' : '#455A64',
      }}
    >
      {ABBR[locationType] ?? locationType}
    </Box>
  );
}

/**
 * @param {{
 *   open: boolean,
 *   order: Object|null,
 *   onClose: () => void,
 *   onSelectStock: (stockLevel: Object, distanceMiles: number) => void,
 *   onRequestSupplierQuote: () => void,
 * }} props
 */
export default function FulfillmentSearchModal({
  open,
  order,
  onClose,
  onSelectStock,
  onRequestSupplierQuote,
}) {
  const [query, setQuery] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setQuery(order?.partNumber ?? '');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // On-site locations assigned to this order's customer or reseller
  const allowedOnSiteLocations = useMemo(() => {
    if (!order) return new Set();
    return new Set(
      customerAssignedLocations
        .filter(
          (cal) =>
            cal.customerName === order.customerName ||
            (order.resellerName && cal.customerName === order.resellerName),
        )
        .map((cal) => cal.locationName),
    );
  }, [order]);

  // Reactive stock search results
  const results = useMemo(() => {
    if (!order) return [];
    const q = query.trim().toLowerCase();
    if (!q) return [];

    const matched = stockLevels.filter((sl) => {
      // On-Site locations only surface when assigned to this order's customer/reseller
      if (sl.locationType === 'On-Site' && !allowedOnSiteLocations.has(sl.locationName)) {
        return false;
      }
      const partDesc = parts.find((p) => p.partNumber === sl.partNumber)?.description ?? '';
      return (
        sl.partNumber.toLowerCase().includes(q) ||
        partDesc.toLowerCase().includes(q)
      );
    });

    return matched
      .map((sl) => {
        const dist = calculateDistance(sl.lat, sl.lon, order.customerLat, order.customerLon);
        const partDesc = parts.find((p) => p.partNumber === sl.partNumber)?.description ?? '';
        return {
          ...sl,
          partDescription: partDesc,
          distanceMiles: dist,
          estShippingCost: estimateShippingCost(dist, sl.shippingCostPerMile),
          estDeliveryDays: estimateDeliveryDays(dist, sl.locationType),
        };
      })
      .sort((a, b) => a.distanceMiles - b.distanceMiles);
  }, [order, query, allowedOnSiteLocations]);

  const handleClose = () => {
    setQuery('');
    onClose();
  };

  const handleSelectStock = (row) => {
    onSelectStock(row, row.distanceMiles);
    setQuery('');
  };

  const handleRequestSupplier = () => {
    setQuery('');
    onClose();
    onRequestSupplierQuote();
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => { if (reason !== 'backdropClick') handleClose(); }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle
        sx={{ display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid var(--grey-200)',
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)' }}>
          Fulfill Order — {order?.ticketNumber}
        </Typography>
        <IconButton size="small" onClick={handleClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: '12px !important', pb: 2 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search by part # or description..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
          sx={{ mb: '20px' }}
        />

        {/* Results table header */}
        <Box
          sx={{ display: 'grid',
            gridTemplateColumns: COLS,
            backgroundColor: '#eef2f6',
            px: 2,
            py: '10px',
            borderRadius: '6px 6px 0 0',
            borderBottom: '1px solid var(--grey-200)',
          }}
        >
          {HEADERS.map((h) => (
            <Typography
              key={h}
              sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--grey-700)' }}
            >
              {h}
            </Typography>
          ))}
        </Box>

        {/* Stock rows */}
        {!query.trim() ? (
          <Box sx={{ py: 3, textAlign: 'center', border: '1px solid var(--grey-200)', borderTop: 'none' }}>
            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>
              Enter a part # or description to search.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ border: '1px solid var(--grey-200)', borderTop: 'none' }}>
            {results.length === 0 ? (
              <Box sx={{ px: 2, py: '10px' }}>
                <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)' }}>
                  No stock found at any location for this part.
                </Typography>
              </Box>
            ) : (
              results.map((row, i) => (
                <Box
                  key={row.id}
                  sx={{ display: 'grid',
                    gridTemplateColumns: COLS,
                    px: 2,
                    py: 0,
                    height: 48,
                    alignItems: 'center',
                    borderBottom: i < results.length - 1 ? '1px solid var(--grey-200)' : 'none',
                    '&:hover': { backgroundColor: '#f8fafc' },
                  }}
                >
                  {/* Location name + abbreviation tag */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-900)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.locationName}
                    </Typography>
                    <LocationTag locationType={row.locationType} />
                  </Box>
                  <Tooltip title={row.partDescription} placement="top" arrow enterDelay={200}>
                    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)', cursor: 'default' }}>
                      {row.partNumber}
                    </Typography>
                  </Tooltip>
                  <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>
                    {Math.round(row.distanceMiles).toLocaleString()} mi
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>
                    {fmt(row.estShippingCost)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>
                    {row.estDeliveryDays}
                  </Typography>
                  <Typography
                    sx={{ fontSize: '0.875rem',
                      color: row.quantityOnHand > 0 ? 'var(--grey-900)' : '#B71C1C',
                    }}
                  >
                    {row.quantityOnHand}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleSelectStock(row)}
                    sx={{ textTransform: 'none',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      borderColor: '#009688',
                      color: '#009688',
                      '&:hover': { backgroundColor: '#e0f2f1', borderColor: '#009688' },
                      py: '3px',
                    }}
                  >
                    Select
                  </Button>
                </Box>
              ))
            )}
          </Box>
        )}

        {/* Supplier CTA */}
        <Box
          sx={{ mt: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Divider sx={{ flex: 1, borderColor: 'var(--grey-200)' }} />
          <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', whiteSpace: 'nowrap', flexShrink: 0 }}>
            Or, source from a supplier
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={handleRequestSupplier}
            sx={{ textTransform: 'none',
              fontSize: '0.8125rem',
              fontWeight: 600,
              borderColor: '#009688',
              color: '#009688',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '&:hover': { backgroundColor: '#e0f2f1', borderColor: '#009688' },
            }}
          >
            Request Supplier Quotes →
          </Button>
          <Divider sx={{ flex: 1, borderColor: 'var(--grey-200)' }} />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Typography
          onClick={handleClose}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}
        >
          Cancel
        </Typography>
      </DialogActions>
    </Dialog>
  );
}
